import { useCallback, useEffect, useState } from 'react'
import {
  useConcreteTestSetTrackerConcreteTestSetsQuery,
  useConcreteTestSetTrackerRelationsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { WithPayItemGlobalId } from 'components/CIMSTable/components/HeaderActionButtons/types'
import { useAnyTrue } from 'hooks/useAnyTrue'
import { BreakDataModalState } from '../components/BreakDataModal/types'
import {
  ConcreteTestSet,
  ConcreteTestSetTrackerContext,
  Relations,
} from './types'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } =
  createStrictContext<ConcreteTestSetTrackerContext>(
    'ConcreteTestSetTrackerContext'
  )

export const useConcreteTestSetTrackerContext = useContextFn

const ConcreteTestSetTrackerProvider: React.FC<WithPayItemGlobalId> = ({
  payItemGlobalId,
  children,
}) => {
  const { projectId } = useProjectContext()
  /**
   * because of how big concrete test sets are, and how many we have to get,
   * we're running simultaneous queries on purpose to get better perceived
   * performance in larger projects
   */
  const {
    data: testSets,
    loading: testSetsFetching,
    refetch: refetchTestSets,
  } = useConcreteTestSetTrackerConcreteTestSetsQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId },
  })
  const {
    data: relations,
    loading: relationsFetching,
    refetch: refetchRelations,
  } = useConcreteTestSetTrackerRelationsQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId },
  })
  const pdfModal = useCIMSModalValues<ConcreteTestSet>()
  const uploadModal = useCIMSModalValues<ConcreteTestSet>()
  const breakDataModal = useCIMSModalValues<BreakDataModalState>()
  const [concreteTestSets, setConcreteTestSets] = useState<ConcreteTestSet[]>(
    []
  )
  const refetch = useCallback<ConcreteTestSetTrackerContext['refetch']>(
    () => Promise.all([refetchTestSets(), refetchRelations()]),
    [refetchRelations, refetchTestSets]
  )
  useEffect(() => {
    const allRelations = [
      ...(relations?.project?.concretePlacements ?? []),
      ...(relations?.project?.concreteTests ?? []),
    ]
    const relationsMap = allRelations.reduce((map, relation) => {
      const key = relation.concreteTestSetGuid
      if (!key) return map
      const found = map.get(key) ?? {
        concretePlacements: [],
        concreteTests: [],
      }
      if (relation.__typename === 'ConcreteTest')
        found.concreteTests.push(relation)
      else if (relation.__typename === 'ConcretePlacement')
        found.concretePlacements.push(relation)
      return map.set(key, found)
    }, new Map<string, Relations>())
    setConcreteTestSets(
      (testSets?.project?.concreteTestSets ?? []).map<ConcreteTestSet>(
        (testSet) => {
          const { concretePlacements = [], concreteTests = [] } =
            relationsMap.get(testSet.globalId) ?? {}
          return {
            ...testSet,
            concretePlacements,
            concreteTests,
          }
        }
      )
    )
  }, [
    relations?.project?.concretePlacements,
    relations?.project?.concreteTests,
    testSets?.project?.concreteTestSets,
  ])
  const [fetching] = useAnyTrue([
    testSetsFetching ?? false,
    relationsFetching ?? false,
  ])
  return (
    <Provider
      value={{
        testSets: { loading: testSetsFetching, refetch: refetchTestSets },
        relations: { loading: relationsFetching, refetch: refetchRelations },
        payItemGlobalId,
        concreteTestSets,
        refetch,
        fetching,
        documentChecklistItems: testSets?.project?.documentChecklistItems,
        pdfModal,
        uploadModal,
        breakDataModal,
      }}
    >
      {children}
    </Provider>
  )
}

export default ConcreteTestSetTrackerProvider
